import React, { useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Card,
  Chip,
  IconButton,
  LineChart,
  ScrollArea,
  StatCard,
} from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useTranslation } from "react-i18next";
import { COMPANY_DASHBOARD } from "graphql/companyConsole";
import dayjs from "utils/dayjs";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import TransactionsTable, {
  TransactionFilterCriteria,
} from "components/common/transactions/TransactionsTable";
import { availableBalance, isCredit } from "./dashboardBalances";
import { ConsoleError } from "./ConsoleState";
import { ConsoleProps, FinancialAccount, dateFilter, money } from "./types";

interface Dashboard {
  currencies: {
    currency: string;
    settledSpend: number;
    moneyIn: number;
    moneyOut: number;
    pendingHolds: number;
    pendingAuthorizations: number;
  }[];
  trend: { date: string; currency: string; settledSpend: number }[];
  accounts: FinancialAccount[];
  partial: boolean;
  startDate: number;
  endDate: number;
  asOf: number;
  activeCards?: number | null;
  frozenCards?: number | null;
}

export function CompanyDashboard({
  companyId,
  params,
  navigate,
  onUpdated,
}: ConsoleProps & {
  onUpdated: (asOf: number) => void;
}) {
  const { t } = useTranslation();
  const currency = (params.get("currency") || "USD").toUpperCase();
  const period = dateFilter(params);
  const filters = useMemo<TransactionFilterCriteria>(
    () => ({
      ...dateFilter(params),
      accountId: params.get("financialAccount") || undefined,
      issuer: params.get("issuer") || undefined,
      currency,
    }),
    [params, currency],
  );
  const { data, loading, error, refetch } = useQuery<{
    companyDashboard: Dashboard;
  }>(COMPANY_DASHBOARD, {
    variables: {
      companyId,
      filter: {
        ...period,
        issuer: filters.issuer,
        financialAccountId: filters.accountId,
        currency,
      },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    fetchPolicy: "no-cache",
  });
  const dashboard = data?.companyDashboard;
  useEffect(() => {
    if (dashboard) onUpdated(dashboard.asOf);
  }, [dashboard, onUpdated]);
  if (error) return <ConsoleError retry={() => void refetch()} />;
  if (loading || !dashboard) return <Skeleton variant="rounded" height={280} />;

  const metrics = dashboard.currencies.find(
    (row) => row.currency.toUpperCase() === currency,
  );
  const accounts = dashboard.accounts.filter(
    (account) => account.currency?.toUpperCase() === currency,
  );
  const cashAccounts = accounts.filter((account) => !isCredit(account));
  const showMoney = (amount?: number) =>
    dashboard.partial || amount == null
      ? t("Unavailable")
      : money(amount, currency);
  const drilldown = (metric: string | null) =>
    navigate({
      tab: "transactions",
      metric,
      currency,
      from: params.get("from"),
      to: params.get("to"),
      type: null,
      status: null,
      search: null,
      min: null,
      max: null,
      cardFilter: null,
    });
  const trendByDate = new Map(
    dashboard.trend
      .filter((row) => row.currency.toUpperCase() === currency)
      .map((row) => [row.date, row.settledSpend]),
  );
  const trend: { date: string; amount: number }[] = [];
  for (
    let day = dayjs(dashboard.startDate).startOf("day");
    day.valueOf() <= dashboard.endDate && trend.length < 93;
    day = day.add(1, "day")
  ) {
    const date = day.format("YYYY-MM-DD");
    trend.push({ date, amount: trendByDate.get(date) ?? 0 });
  }
  const tickCount = Math.min(5, trend.length);
  const dateTicks = new Set(
    Array.from({ length: tickCount }, (_, index) =>
      tickCount === 1
        ? 0
        : Math.round((index * (trend.length - 1)) / (tickCount - 1)),
    ),
  );
  const balanceTimes = cashAccounts.flatMap((account) =>
    account.balance?.reportedAt == null ? [] : [account.balance.reportedAt],
  );
  const balanceTime = balanceTimes.length
    ? new Date(Math.min(...balanceTimes)).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : null;

  return (
    <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
      <Stack gap="var(--space-5)" sx={{ pb: "var(--space-3)" }}>
        {dashboard.partial && (
          <Alert severity="warning">
            {t(
              "This period exceeds the available reporting coverage, Narrow the date range to see complete totals",
            )}
          </Alert>
        )}
        {accounts.some((account) => account.stale) && (
          <Alert severity="warning">
            {t(
              "Some balances are older issuer readings, check their as-of times",
            )}
          </Alert>
        )}
        {accounts.some((account) => !account.available) && (
          <Alert severity="warning">
            {t("Some issuer balances are unavailable")}
          </Alert>
        )}
        <Columns count={{ xs: 1, sm: 2, lg: 4 }} gap="var(--space-4)">
          <StatCard
            title={t("Cash available")}
            value={showMoney(availableBalance(accounts, false))}
            caption={`${balanceTime ? `${t("As of")} ${balanceTime} · ` : ""}${t("CompanyConsole_cashAccounts", { count: cashAccounts.length })}`}
            tooltip={t("Cash balances exclude available credit")}
            action={{
              type: "open",
              onClick: () => navigate({ tab: "accounts", currency }),
            }}
          />
          <StatCard
            title={t("Settled card spend")}
            value={showMoney(metrics?.settledSpend ?? 0)}
            caption={t("Net of settled refunds")}
            tooltip={t("Settled card spend, net of refunds")}
            action={{ type: "open", onClick: () => drilldown("SPEND") }}
          />
          <StatCard
            title={t("Pending holds")}
            value={showMoney(metrics?.pendingHolds ?? 0)}
            caption={t("CompanyConsole_pendingAuthorizations", {
              count: metrics?.pendingAuthorizations ?? 0,
            })}
            tooltip={t("Pending authorizations")}
            action={{ type: "open", onClick: () => drilldown("HOLDS") }}
          />
          <StatCard
            title={t("Active cards")}
            value={
              dashboard.activeCards == null
                ? t("Unavailable")
                : String(dashboard.activeCards)
            }
            caption={`${dashboard.frozenCards == null ? t("Unavailable") : t("CompanyConsole_frozenCards", { count: dashboard.frozenCards })} · ${currency}`}
            action={{
              type: "open",
              onClick: () =>
                navigate({
                  tab: "cards",
                  status: "ACTIVE",
                  currency,
                  metric: null,
                  search: null,
                }),
            }}
          />
        </Columns>
        <Columns
          count={{ xs: 1, md: "minmax(0, 2fr) minmax(0, 1fr)" }}
          gap="var(--space-4)"
        >
          <Card
            title={t("Spend Velocity")}
            subtitle={t("Settled card spend, net of refunds")}
            action={{
              type: "content",
              content: <Chip size="sm" label={currency} />,
            }}
          >
            <Typography variant="h2">
              {showMoney(metrics?.settledSpend ?? 0)}
            </Typography>
            {dashboard.partial ? (
              <Alert severity="info">
                {t("Narrow the date range to view the chart")}
              </Alert>
            ) : (
              <LineChart
                height={220}
                margin={{ right: 32 }}
                series={[
                  {
                    id: "spend",
                    label: t("Settled card spend"),
                    data: trend.map((row) => row.amount),
                    area: true,
                    showMark: false,
                    curve: "monotone",
                    valueFormatter: (value) => money(value, currency),
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "point",
                    data: trend.map((row) => row.date),
                    tickInterval: (_date, index) => dateTicks.has(index),
                    valueFormatter: (date: string | number) =>
                      new Date(`${date}T00:00:00Z`).toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric", timeZone: "UTC" },
                      ),
                  },
                ]}
                yAxis={[
                  {
                    valueFormatter: (value: string | number) =>
                      money(Number(value), currency),
                  },
                ]}
                legend={false}
                grid={{ horizontal: true }}
              />
            )}
          </Card>
          <Card
            title={t("Money movement")}
            subtitle={t("External transfers and payments")}
          >
            <Stack gap="var(--space-5)">
              {(
                [
                  ["Money in", metrics?.moneyIn ?? 0, "MONEY_IN"],
                  ["Money out", metrics?.moneyOut ?? 0, "MONEY_OUT"],
                ] as const
              ).map(([title, amount, metric]) => (
                <React.Fragment key={metric}>
                  <Inline justify="between" wrap={false}>
                    <Stack gap="var(--space-1)">
                      <Typography variant="caption">{t(title)}</Typography>
                      <Typography variant="h3">{showMoney(amount)}</Typography>
                    </Stack>
                    <IconButton
                      variant="outlined"
                      aria-label={t(title)}
                      onClick={() => drilldown(metric)}
                    >
                      <HugeiconsIcon icon={ArrowRight02Icon} size={18} />
                    </IconButton>
                  </Inline>
                  <Divider />
                </React.Fragment>
              ))}
              <Typography variant="caption">
                {t("Internal transfers and card spend excluded")}
              </Typography>
            </Stack>
          </Card>
        </Columns>
        <Card
          title={t("Recent transactions")}
          subtitle={t("Latest activity across the selected accounts")}
          action={{
            type: "content",
            content: (
              <Button
                variant="text"
                size="small"
                endIcon={<HugeiconsIcon icon={ArrowRight02Icon} size={18} />}
                onClick={() => drilldown(null)}
              >
                {t("View all transactions")}
              </Button>
            ),
          }}
        >
          <Box sx={{ height: 340, minHeight: 0 }}>
            <TransactionsTable
              selectedCompanyId={companyId}
              filterCriteria={filters}
              onRowClick={(id) => navigate({ transaction: id })}
            />
          </Box>
        </Card>
      </Stack>
    </ScrollArea>
  );
}
